import { useUser } from "@clerk/clerk-react";
import { Button, CircularProgress, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { GfgTextField } from "@/components/ui";
import UserAvatar from "@/components/users/UserAvatar";
import { usePermissions } from "@/hooks/usePermissions";

export function ProfileForm() {
  const { user } = useUser();
  const { userId } = usePermissions();
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const primaryEmail = user?.primaryEmailAddress?.emailAddress || "";

  const handleSave = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      await user.update({ firstName, lastName });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;
    setIsLoading(true);
    try {
      await user.setProfileImage({ file });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      await user.setProfileImage({ file: null });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{ maxWidth: 680, padding: "26px 28px", borderRadius: 12 }}
      className="w-full bg-white border border-gray-200 shadow-sm"
    >
      <Typography
        variant="h5"
        component="h2"
        className="mb-6 text-gray-900"
        sx={{ fontFamily: "var(--font-crimson)", fontSize: "20px", fontWeight: 400 }}
      >
        Profile details
      </Typography>

      {/* Image Upload Row */}
      <div className="flex items-center gap-3 mb-8">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: "90px" }}>
          Profile image
        </Typography>
        <UserAvatar
          imageUrl={user?.imageUrl}
          hasImage={user?.hasImage}
          seed={userId}
          size={48}
          alt="Profile"
          className="border border-gray-200"
        />
        <Button
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
          sx={{
            color: "var(--color-rodeo-black)",
            borderColor: "var(--color-smoke)",
            textTransform: "uppercase",
            fontWeight: 700,
            fontSize: "11px",
            letterSpacing: "1.5px",
            padding: "8px 14px",
            "&:hover": {
              borderColor: "var(--color-graphite)",
              backgroundColor: "var(--color-background)",
            },
          }}
        >
          Upload Image
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
          className="hidden"
        />
        <button
          type="button"
          onClick={handleRemoveImage}
          disabled={isLoading}
          className="text-sm font-medium text-good-wine hover:text-good-wine-2 disabled:opacity-50"
        >
          Remove
        </button>
      </div>

      {/* Name Row */}
      <div className="flex flex-col sm:flex-row gap-[14px] mb-[18px]">
        <GfgTextField
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={isLoading}
          surface="white"
        />
        <GfgTextField
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={isLoading}
          surface="white"
        />
      </div>

      {/* Email Row */}
      <div className="mb-[18px]">
        <GfgTextField
          label="Email address"
          value={primaryEmail}
          disabled
          surface="white"
          inputClassName="disabled:text-graphite"
          // WebKit/Blink paint their own washed-out grey over `color` on a disabled input, so
          // `disabled:text-graphite` alone does not hold: -webkit-text-fill-color is the only
          // property that wins. Inline rather than a `disabled:` variant because this field is
          // permanently disabled — the address is owned by Clerk and is not editable here.
          style={{ WebkitTextFillColor: "var(--color-graphite)" }}
        />
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading}
          sx={{
            backgroundColor: "var(--color-rodeo-black)",
            color: "var(--color-white)",
            textTransform: "uppercase",
            fontWeight: 700,
            fontSize: "11px",
            letterSpacing: "1.5px",
            padding: "10px 20px",
            borderRadius: "0.375rem",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "var(--color-background-dark)",
              boxShadow: "none",
            },
          }}
        >
          {isLoading ? <CircularProgress aria-label="Loading" size={20} color="inherit" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}
